// @ts-nocheck
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import AdminTopNav from "./AdminTopNav";
import "./AdminBrandMyLaptop.css";

interface Slot {
    slotId: string;
    section: "lid" | "palm_left" | "palm_right" | "charger" | "mouse" | string;
    name: string;
    dimensions: string;
    price: number; // USD
    inrPrice?: number;
    eurPrice?: number;
    currency: string;
    isBooked: boolean;
    status: "available" | "under_review" | "accepted" | "rejected" | "action_required" | string;
    statusMessage?: string;
    sponsor?: {
        companyName?: string;
        website?: string;
        logoUrl?: string;
        bookedAt?: string;
        contactEmail?: string;
    };
}

interface PricingPackage {
    _id?: string;
    packageId: string;
    name: string;
    description: string;
    slots: Slot[];
}

const DEFAULT_INR_MAP: Record<string, number> = {
    "lid-1": 2723, "lid-2": 3645, "lid-3": 2498,
    "lid-4": 749, "lid-4b": 598, "lid-5": 603, "lid-5b": 812,
    "lid-6": 1053, "lid-7": 1121, "lid-8": 1099,
    "palm-left-1": 142, "palm-left-2": 128, "palm-left-3": 139, "palm-left-4": 117,
    "palm-right-1": 153, "palm-right-2": 133, "palm-right-3": 136, "palm-right-4": 121,
    "charger-1": 582, "mouse-1": 322
};

const getSlotInrPrice = (slot: Slot) => {
    return slot.inrPrice || DEFAULT_INR_MAP[slot.slotId] || Math.round((slot.price || 0) * 2.5);
};

const getSlotUsdPrice = (slot: Slot) => {
    if (slot.price && slot.price !== 199 && slot.price !== 299) return slot.price;
    const inr = getSlotInrPrice(slot);
    return Math.round(inr / 2.5);
};

const getSlotEurPrice = (slot: Slot) => {
    if (slot.eurPrice) return slot.eurPrice;
    const usd = getSlotUsdPrice(slot);
    return Math.round(usd * 1.11);
};

interface Campaign {
    _id: string;
    title: string;
    description: string;
    destination: string;
    packageType?: string;
    startDate?: string;
    endDate?: string;
    bookingOpenDate?: string;
    bookingCloseDate?: string;
    status: "open" | "extended" | "closed" | "completed";
    isExtended: boolean;
    slots: Slot[];
    proofOfWork?: {
        photos: string[];
        recapText: string;
        publishedAt?: string;
    };
    createdAt: string;
}

export default function AdminBrandMyLaptop() {
    const [navPinned, setNavPinned] = useState(false);
    const [activeViewTab, setActiveViewTab] = useState<"events" | "packages">("events");

    // Campaigns state
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);

    // Packages state
    const [packages, setPackages] = useState<PricingPackage[]>([]);
    const [packagesLoading, setPackagesLoading] = useState<boolean>(false);
    const [selectedPackageToEdit, setSelectedPackageToEdit] = useState<string>("brand_slot");
    const [pkgNameEdit, setPkgNameEdit] = useState<string>("");
    const [pkgDescEdit, setPkgDescEdit] = useState<string>("");
    const [pkgSlotsEdit, setPkgSlotsEdit] = useState<Slot[]>([]);
    const [savingPkg, setSavingPkg] = useState<boolean>(false);
    const [pkgFeedbackSuccess, setPkgFeedbackSuccess] = useState<string>("");
    const [showCreatePkgModal, setShowCreatePkgModal] = useState<boolean>(false);
    const [newPkgName, setNewPkgName] = useState<string>("");
    const [newPkgDesc, setNewPkgDesc] = useState<string>("");

    // Edit Created Travel Event Details state
    const [showEditCampModal, setShowEditCampModal] = useState<boolean>(false);
    const [editCampId, setEditCampId] = useState<string>("");
    const [editCampTitle, setEditCampTitle] = useState<string>("");
    const [editCampDesc, setEditCampDesc] = useState<string>("");
    const [editCampDestination, setEditCampDestination] = useState<string>("");
    const [editCampStartDate, setEditCampStartDate] = useState<string>("");
    const [editCampEndDate, setEditCampEndDate] = useState<string>("");
    const [editCampBookingOpenDate, setEditCampBookingOpenDate] = useState<string>("");
    const [editCampBookingCloseDate, setEditCampBookingCloseDate] = useState<string>("");
    const [editCampStatus, setEditCampStatus] = useState<string>("open");
    const [editCampIsExtended, setEditCampIsExtended] = useState<boolean>(false);
    const [editCampPackageType, setEditCampPackageType] = useState<string>("brand_slot");
    const [savingCampEdit, setSavingCampEdit] = useState<boolean>(false);

    // Modal state for creating a new weekly campaign
    const [showNewModal, setShowNewModal] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const [newDesc, setNewDesc] = useState("");
    const [newDestination, setNewDestination] = useState("");
    const [newStartDate, setNewStartDate] = useState("");
    const [newEndDate, setNewEndDate] = useState("");
    const [newBookingCloseDate, setNewBookingCloseDate] = useState("");
    const [newPackageType, setNewPackageType] = useState<string>("brand_slot");

    // Proof-of-work state
    const [showProofModal, setShowProofModal] = useState(false);
    const [proofRecap, setProofRecap] = useState("");
    const [proofFiles, setProofFiles] = useState<FileList | null>(null);
    const [submittingProof, setSubmittingProof] = useState(false);

    // Slot status feedback modal state
    const [statusModalSlot, setStatusModalSlot] = useState<Slot | null>(null);
    const [statusTargetStatus, setStatusTargetStatus] = useState<string>("");
    const [statusFeedbackMessage, setStatusFeedbackMessage] = useState<string>("");
    const [savingStatus, setSavingStatus] = useState<boolean>(false);

    const fetchCampaigns = async () => {
        try {
            setLoading(true);
            const res = await axios.get(API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_CAMPAIGNS);
            if (res.data && res.data.success) {
                const list = res.data.campaigns || [];
                setCampaigns(list);
            }
        } catch (err) {
            console.error("Error fetching campaigns:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPackages = async () => {
        try {
            setPackagesLoading(true);
            const res = await axios.get(API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_PACKAGES);
            if (res.data && res.data.success) {
                const list: PricingPackage[] = res.data.packages || [];
                setPackages(list);
                if (list.length > 0) {
                    const current = list.find((p) => p.packageId === selectedPackageToEdit) || list[0];
                    setSelectedPackageToEdit(current.packageId);
                    setPkgNameEdit(current.name);
                    setPkgDescEdit(current.description || "");
                    setPkgSlotsEdit(JSON.parse(JSON.stringify(current.slots || [])));
                }
            }
        } catch (err) {
            console.error("Error fetching packages:", err);
        } finally {
            setPackagesLoading(false);
        }
    };

    useEffect(() => {
        fetchCampaigns();
        fetchPackages();
    }, []);

    const activeCampaign = selectedCampaignId
        ? campaigns.find((c) => c._id === selectedCampaignId) || null
        : null;

    // Toggle deadline extension
    const handleToggleExtension = async () => {
        if (!activeCampaign) return;
        const newExtended = !activeCampaign.isExtended;
        const newStatus = newExtended ? "extended" : "open";

        try {
            const res = await axios.put(
                API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_CAMPAIGN_UPDATE(activeCampaign._id),
                {
                    isExtended: newExtended,
                    status: newStatus
                }
            );
            if (res.data && res.data.success) {
                await fetchCampaigns();
            }
        } catch (err) {
            console.error("Error extending campaign:", err);
        }
    };

    // Close booking
    const handleCloseBooking = async () => {
        if (!activeCampaign) return;
        try {
            const res = await axios.put(
                API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_CAMPAIGN_UPDATE(activeCampaign._id),
                {
                    status: "closed",
                    isExtended: false
                }
            );
            if (res.data && res.data.success) {
                await fetchCampaigns();
            }
        } catch (err) {
            console.error("Error closing booking:", err);
        }
    };

    // Change event pricing package for active campaign
    const handlePackageChange = async (packageType: string) => {
        if (!activeCampaign) return;
        try {
            const res = await axios.put(
                API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_CAMPAIGN_UPDATE(activeCampaign._id),
                { packageType }
            );
            if (res.data && res.data.success) {
                await fetchCampaigns();
            }
        } catch (err) {
            console.error("Error updating event package:", err);
        }
    };

    // Open Edit Travel Event Details modal
    const openEditCampaign = (camp: Campaign) => {
        setEditCampId(camp._id);
        setEditCampTitle(camp.title || "");
        setEditCampDesc(camp.description || "");
        setEditCampDestination(camp.destination || "");
        setEditCampStartDate(camp.startDate ? camp.startDate.slice(0, 10) : "");
        setEditCampEndDate(camp.endDate ? camp.endDate.slice(0, 10) : "");
        setEditCampBookingOpenDate(camp.bookingOpenDate ? camp.bookingOpenDate.slice(0, 10) : "");
        setEditCampBookingCloseDate(camp.bookingCloseDate ? camp.bookingCloseDate.slice(0, 10) : "");
        setEditCampStatus(camp.status || "open");
        setEditCampIsExtended(Boolean(camp.isExtended));
        setEditCampPackageType(camp.packageType || "brand_slot");
        setShowEditCampModal(true);
    };

    // Save Edit Travel Event Details
    const handleSaveCampaignEdit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editCampId) return;
        setSavingCampEdit(true);
        try {
            const res = await axios.put(
                API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_CAMPAIGN_UPDATE(editCampId),
                {
                    title: editCampTitle,
                    description: editCampDesc,
                    destination: editCampDestination,
                    startDate: editCampStartDate || undefined,
                    endDate: editCampEndDate || undefined,
                    bookingOpenDate: editCampBookingOpenDate || undefined,
                    bookingCloseDate: editCampBookingCloseDate || undefined,
                    status: editCampStatus,
                    isExtended: editCampIsExtended,
                    packageType: editCampPackageType
                }
            );
            if (res.data && res.data.success) {
                setShowEditCampModal(false);
                await fetchCampaigns();
            }
        } catch (err) {
            console.error("Error saving campaign edit:", err);
            alert("Failed to update travel event details.");
        } finally {
            setSavingCampEdit(false);
        }
    };

    // Handle initiating slot status change
    const handleInitiateStatusChange = (slot: Slot, newStatus: string) => {
        if (newStatus === "rejected" || newStatus === "action_required") {
            setStatusModalSlot(slot);
            setStatusTargetStatus(newStatus);
            setStatusFeedbackMessage(slot.statusMessage || "");
        } else {
            executeStatusUpdate(slot.slotId, newStatus, "");
        }
    };

    // Save slot status & feedback message to backend
    const executeStatusUpdate = async (slotId: string, status: string, message: string) => {
        if (!activeCampaign) return;
        try {
            setSavingStatus(true);
            const res = await axios.post(
                API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_SLOT_STATUS(activeCampaign._id, slotId),
                { status, statusMessage: message }
            );
            if (res.data && res.data.success) {
                setStatusModalSlot(null);
                setStatusTargetStatus("");
                setStatusFeedbackMessage("");
                await fetchCampaigns();
            }
        } catch (err) {
            console.error("Error updating slot status:", err);
            alert("Failed to update status. Please try again.");
        } finally {
            setSavingStatus(false);
        }
    };

    // Reset a slot
    const handleResetSlot = async (slotId: string) => {
        if (!activeCampaign) return;
        if (!window.confirm(`Are you sure you want to reset and free up slot ${slotId}?`)) return;

        try {
            const res = await axios.post(
                API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_SLOT_RESET(activeCampaign._id, slotId),
                {}
            );
            if (res.data && res.data.success) {
                await fetchCampaigns();
            }
        } catch (err) {
            console.error("Error resetting slot:", err);
        }
    };

    // Create New Weekly Campaign
    const handleCreateCampaign = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await axios.post(API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_CAMPAIGN_CREATE, {
                title: newTitle,
                description: newDesc,
                destination: newDestination,
                startDate: newStartDate,
                endDate: newEndDate,
                bookingCloseDate: newBookingCloseDate,
                packageType: newPackageType,
                makeActive: true
            });

            if (res.data && res.data.success) {
                setShowNewModal(false);
                setNewTitle("");
                setNewDesc("");
                setNewDestination("");
                setNewStartDate("");
                setNewEndDate("");
                setNewBookingCloseDate("");
                setNewPackageType("brand_slot");
                await fetchCampaigns();
                if (res.data.campaign?._id) {
                    setSelectedCampaignId(res.data.campaign._id);
                }
            }
        } catch (err) {
            console.error("Error creating campaign:", err);
        }
    };

    // Submit Proof of Work
    const handleSubmitProof = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!activeCampaign) return;
        setSubmittingProof(true);

        try {
            const formData = new FormData();
            formData.append("recapText", proofRecap);
            formData.append("markCompleted", "true");
            if (proofFiles) {
                for (let i = 0; i < proofFiles.length; i++) {
                    formData.append("photos", proofFiles[i]);
                }
            }

            const res = await axios.post(
                API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_CAMPAIGN_PROOF(activeCampaign._id),
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            if (res.data && res.data.success) {
                setShowProofModal(false);
                setProofRecap("");
                setProofFiles(null);
                await fetchCampaigns();
            }
        } catch (err) {
            console.error("Error uploading proof of work:", err);
        } finally {
            setSubmittingProof(false);
        }
    };

    // Package Editing Handlers
    const handleSelectPackageToEdit = (pkgId: string) => {
        setSelectedPackageToEdit(pkgId);
        const current = packages.find((p) => p.packageId === pkgId);
        if (current) {
            setPkgNameEdit(current.name);
            setPkgDescEdit(current.description || "");
            setPkgSlotsEdit(JSON.parse(JSON.stringify(current.slots || [])));
            setPkgFeedbackSuccess("");
        }
    };

    const handleSlotInrChange = (slotId: string, val: number) => {
        setPkgSlotsEdit((prev) =>
            prev.map((s) => {
                if (s.slotId === slotId) {
                    const inr = Math.max(0, val);
                    const usd = Math.round(inr / 2.5);
                    const eur = Math.round(usd * 1.11);
                    return { ...s, inrPrice: inr, price: usd, eurPrice: eur };
                }
                return s;
            })
        );
    };

    const handleSlotUsdChange = (slotId: string, val: number) => {
        setPkgSlotsEdit((prev) =>
            prev.map((s) => {
                if (s.slotId === slotId) {
                    const usd = Math.max(0, val);
                    const eur = Math.round(usd * 1.11);
                    return { ...s, price: usd, eurPrice: eur };
                }
                return s;
            })
        );
    };

    const handleSlotEurChange = (slotId: string, val: number) => {
        setPkgSlotsEdit((prev) =>
            prev.map((s) => {
                if (s.slotId === slotId) {
                    return { ...s, eurPrice: Math.max(0, val) };
                }
                return s;
            })
        );
    };

    const handleSavePackage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedPackageToEdit) return;
        setSavingPkg(true);
        setPkgFeedbackSuccess("");
        try {
            const res = await axios.put(
                API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_PACKAGE_UPDATE(selectedPackageToEdit),
                {
                    name: pkgNameEdit,
                    description: pkgDescEdit,
                    slots: pkgSlotsEdit,
                    applyToActiveCampaign: true
                }
            );
            if (res.data && res.data.success) {
                setPkgFeedbackSuccess("Package rates saved successfully & synced with unbooked campaign slots!");
                await fetchPackages();
                await fetchCampaigns();
                setTimeout(() => setPkgFeedbackSuccess(""), 4000);
            }
        } catch (err: any) {
            console.error("Error saving package:", err);
            alert(err.response?.data?.message || "Failed to save package.");
        } finally {
            setSavingPkg(false);
        }
    };

    const handleCreatePackage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newPkgName.trim()) return;
        try {
            const pkgId = newPkgName.toLowerCase().replace(/[^a-z0-9]/g, "_").trim();
            const res = await axios.post(API_ENDPOINTS.BRAND_MY_LAPTOP.ADMIN_PACKAGE_CREATE, {
                packageId: pkgId,
                name: newPkgName.trim(),
                description: newPkgDesc.trim(),
                slots: pkgSlotsEdit.length > 0 ? pkgSlotsEdit : undefined
            });
            if (res.data && res.data.success) {
                setShowCreatePkgModal(false);
                setNewPkgName("");
                setNewPkgDesc("");
                await fetchPackages();
                if (res.data.package?.packageId) {
                    handleSelectPackageToEdit(res.data.package.packageId);
                }
            }
        } catch (err: any) {
            console.error("Error creating package:", err);
            alert(err.response?.data?.message || "Failed to create package.");
        }
    };

    const bookedSlots = activeCampaign ? activeCampaign.slots.filter((s) => s.isBooked && s.status !== "available") : [];

    // Section definitions for package editor table
    const sectionGroups = [
        { key: "lid_top", title: "💻 Outside Laptop Lid — Top Row", slotIds: ["lid-1", "lid-2", "lid-3"] },
        { key: "lid_mid", title: "💻 Outside Laptop Lid — Middle Row", slotIds: ["lid-4", "lid-4b", "lid-5", "lid-5b"] },
        { key: "lid_bot", title: "💻 Outside Laptop Lid — Bottom Row", slotIds: ["lid-6", "lid-7", "lid-8"] },
        { key: "palm_left", title: "🖐️ Inside Deck — Left Palm Rest", slotIds: ["palm-left-1", "palm-left-2", "palm-left-3", "palm-left-4"] },
        { key: "palm_right", title: "🖐️ Inside Deck — Right Palm Rest", slotIds: ["palm-right-1", "palm-right-2", "palm-right-3", "palm-right-4"] },
        { key: "accessories", title: "🔌 Accessories (Charger & Magic Mouse)", slotIds: ["charger-1", "mouse-1"] }
    ];

    return (
        <div className="admin-bml-container">
            <AdminTopNav onPinChange={setNavPinned} />

            {/* Header */}
            <div className="admin-bml-header">
                <div>
                    <h1>Brand My Laptop & Surface Sponsorship</h1>
                    <p>
                        Manage travel tours, edit custom package rates (INR, USD, EUR), and approve brand sticker bookings.
                    </p>
                </div>
                <div className="admin-bml-header-actions">
                    <button
                        className="admin-bml-btn primary"
                        onClick={() => setShowNewModal(true)}
                    >
                        <i className="fi fi-rr-plus"></i> New Weekly Event
                    </button>
                </div>
            </div>

            {/* Top Navigation Tabs: Events vs Package Pricing */}
            <div className="admin-bml-top-tabs">
                <button
                    className={`admin-bml-tab-btn ${activeViewTab === "events" ? "active" : ""}`}
                    onClick={() => setActiveViewTab("events")}
                >
                    <i className="fi fi-rr-calendar"></i> Travel Events & Slot Bookings
                </button>
                <button
                    className={`admin-bml-tab-btn ${activeViewTab === "packages" ? "active" : ""}`}
                    onClick={() => setActiveViewTab("packages")}
                >
                    <i className="fi fi-rr-settings-sliders"></i> Edit Package Pricing ({packages.length})
                </button>
            </div>

            {/* VIEW 1: PACKAGE PRICING EDITOR */}
            {activeViewTab === "packages" ? (
                <div className="admin-pkg-editor-card">
                    <div className="admin-pkg-tabs-row">
                        <div className="admin-pkg-pills">
                            {packages.map((pkg) => (
                                <button
                                    key={pkg.packageId}
                                    type="button"
                                    className={`admin-pkg-pill-btn ${selectedPackageToEdit === pkg.packageId ? "active" : ""}`}
                                    onClick={() => handleSelectPackageToEdit(pkg.packageId)}
                                >
                                    <i className="fi fi-rr-gem"></i> {pkg.name}
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            className="admin-bml-btn secondary"
                            onClick={() => setShowCreatePkgModal(true)}
                        >
                            <i className="fi fi-rr-plus"></i> + Create New Pricing Package
                        </button>
                    </div>

                    {pkgFeedbackSuccess && (
                        <div className="admin-alert-success">
                            <i className="fi fi-rr-check-circle" style={{ fontSize: 18 }}></i>
                            {pkgFeedbackSuccess}
                        </div>
                    )}

                    <form onSubmit={handleSavePackage}>
                        <div className="admin-pkg-form-grid">
                            <div className="admin-form-group">
                                <label>Package Name *</label>
                                <input
                                    type="text"
                                    value={pkgNameEdit}
                                    onChange={(e) => setPkgNameEdit(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Description / Event Context</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Full conference rates for major tech summits."
                                    value={pkgDescEdit}
                                    onChange={(e) => setPkgDescEdit(e.target.value)}
                                />
                            </div>
                        </div>

                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: 16, color: "#0f172a" }}>Slot Rates Configuration</h3>
                                <p style={{ margin: "2px 0 0", fontSize: 13, color: "#64748b" }}>
                                    Editing <strong>INR (₹)</strong> automatically calculates <strong>USD ($)</strong> at INR / 2.5 and <strong>EUR (€)</strong> at +11% over USD.
                                </p>
                            </div>
                            <button
                                type="submit"
                                className="admin-bml-btn primary"
                                disabled={savingPkg}
                            >
                                {savingPkg ? "Saving Rates..." : "Save Package Pricing"}
                            </button>
                        </div>

                        <div className="admin-price-table-wrap">
                            <table className="admin-price-table">
                                <thead>
                                    <tr>
                                        <th>Slot Name & ID</th>
                                        <th>Dimensions</th>
                                        <th>INR Price (₹)</th>
                                        <th>USD Price ($)</th>
                                        <th>EUR Price (€)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sectionGroups.map((group) => {
                                        const groupSlots = pkgSlotsEdit.filter((s) => group.slotIds.includes(s.slotId));
                                        if (groupSlots.length === 0) return null;
                                        return (
                                            <React.Fragment key={group.key}>
                                                <tr className="admin-price-section-header">
                                                    <td colSpan={5}>{group.title}</td>
                                                </tr>
                                                {groupSlots.map((slot) => {
                                                    const inr = slot.inrPrice !== undefined ? slot.inrPrice : (DEFAULT_INR_MAP[slot.slotId] || 100);
                                                    const usd = slot.price !== undefined ? slot.price : Math.round(inr / 2.5);
                                                    const eur = slot.eurPrice !== undefined ? slot.eurPrice : Math.round(usd * 1.11);
                                                    return (
                                                        <tr key={slot.slotId}>
                                                            <td>
                                                                <strong>{slot.name}</strong>
                                                                <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: 6 }}>({slot.slotId})</span>
                                                            </td>
                                                            <td style={{ color: "#64748b", fontSize: 12 }}>{slot.dimensions}</td>
                                                            <td>
                                                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                                    <span>₹</span>
                                                                    <input
                                                                        type="number"
                                                                        className="admin-price-input"
                                                                        value={inr}
                                                                        onChange={(e) => handleSlotInrChange(slot.slotId, Number(e.target.value))}
                                                                    />
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                                    <span>$</span>
                                                                    <input
                                                                        type="number"
                                                                        className="admin-price-input"
                                                                        value={usd}
                                                                        onChange={(e) => handleSlotUsdChange(slot.slotId, Number(e.target.value))}
                                                                    />
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                                    <span>€</span>
                                                                    <input
                                                                        type="number"
                                                                        className="admin-price-input"
                                                                        value={eur}
                                                                        onChange={(e) => handleSlotEurChange(slot.slotId, Number(e.target.value))}
                                                                    />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                            <button
                                type="submit"
                                className="admin-bml-btn primary"
                                disabled={savingPkg}
                            >
                                {savingPkg ? "Saving Rates..." : "Save Package Pricing"}
                            </button>
                        </div>
                    </form>
                </div>
            ) : (
                /* VIEW 2: TRAVEL EVENTS & SLOTS */
                <>
                    {!selectedCampaignId || !activeCampaign ? (
                        /* OVERVIEW OF ALL EVENTS */
                        <>
                            <div className="admin-back-nav-row">
                                <span style={{ fontSize: 14, color: "#64748b" }}>
                                    Select an event to view and manage its slots and sponsor assignments.
                                </span>
                                <a
                                    href="/app-store/brand-my-laptop"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="admin-bml-btn secondary"
                                    style={{ padding: "6px 14px", fontSize: 13 }}
                                >
                                    <i className="fi fi-rr-eye"></i> View Public Page
                                </a>
                            </div>

                            {loading ? (
                                <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
                                    Loading events...
                                </div>
                            ) : campaigns.length === 0 ? (
                                <div className="admin-empty-events-box">
                                    <i className="fi fi-rr-calendar" style={{ fontSize: 40, color: "#94a3b8" }}></i>
                                    <h3>No Events Created Yet</h3>
                                    <p>Click "New Weekly Event" to launch your first sponsorship campaign!</p>
                                    <button
                                        className="admin-bml-btn primary"
                                        style={{ marginTop: 12 }}
                                        onClick={() => setShowNewModal(true)}
                                    >
                                        <i className="fi fi-rr-plus"></i> Launch First Event
                                    </button>
                                </div>
                            ) : (
                                <div className="admin-bml-card">
                                    <h2>
                                        <i className="fi fi-rr-calendar" style={{ color: "#2563eb" }}></i>
                                        All Weekly Travel Campaigns ({campaigns.length})
                                    </h2>
                                    <p style={{ fontSize: 13, color: "#64748b", margin: "-6px 0 16px" }}>
                                        Click on any event row to open its <strong>Package Pricing</strong> and <strong>Interactive Slot Management Table</strong>.
                                    </p>

                                    <div className="admin-table-scroll-wrapper">
                                        <table className="admin-events-overview-table">
                                            <thead>
                                                <tr>
                                                    <th>Event / Trip Title</th>
                                                    <th>Destination</th>
                                                    <th>Package Tier</th>
                                                    <th>Status</th>
                                                    <th>Booked Slots</th>
                                                    <th>Revenue</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {campaigns.map((c) => {
                                                    const cBooked = c.slots?.filter((s) => s.isBooked && s.status !== "available") || [];
                                                    const totalInr = cBooked.reduce((acc, s) => acc + getSlotInrPrice(s), 0);
                                                    const matchedPkg = packages.find((p) => p.packageId === c.packageType);
                                                    const pkgDisplayName = matchedPkg ? matchedPkg.name : (c.packageType === "regular_event" ? "Regular Event Package" : "Brand Slot Package");
                                                    return (
                                                        <tr
                                                            key={c._id}
                                                            className="admin-event-clickable-row"
                                                            onClick={() => setSelectedCampaignId(c._id)}
                                                        >
                                                            <td>
                                                                <strong style={{ color: "#0f172a", fontSize: 14 }}>{c.title}</strong>
                                                                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                                                                    {c.startDate ? new Date(c.startDate).toLocaleDateString() : "TBD"} — {c.endDate ? new Date(c.endDate).toLocaleDateString() : "TBD"}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <i className="fi fi-rr-marker" style={{ marginRight: 4, color: "#64748b" }}></i>
                                                                {c.destination}
                                                            </td>
                                                            <td>
                                                                <span className="admin-pkg-chip pkg-brand">
                                                                    {pkgDisplayName}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className={`admin-event-status-pill ${c.status}`}>
                                                                    {c.status.toUpperCase()} {c.isExtended ? "(EXT)" : ""}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <strong>{cBooked.length}</strong> / {c.slots?.length || 20} Slots
                                                            </td>
                                                            <td>
                                                                <strong style={{ color: "#10b981" }}>₹{totalInr.toLocaleString()}</strong>
                                                            </td>
                                                            <td>
                                                                <div style={{ display: "flex", gap: 6 }}>
                                                                    <button
                                                                        className="admin-bml-btn primary"
                                                                        style={{ padding: "6px 12px", fontSize: 12 }}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedCampaignId(c._id);
                                                                        }}
                                                                    >
                                                                        Manage Slots →
                                                                    </button>
                                                                    <button
                                                                        className="admin-bml-btn secondary"
                                                                        style={{ padding: "6px 10px", fontSize: 12 }}
                                                                        title="Edit Travel Event Details"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            openEditCampaign(c);
                                                                        }}
                                                                    >
                                                                        <i className="fi fi-rr-edit"></i>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        /* DETAIL VIEW WITH PACKAGE SELECTION & SLOTS TABLE */
                        <>
                            {/* Back button row */}
                            <div className="admin-back-nav-row">
                                <button
                                    className="admin-back-btn"
                                    onClick={() => setSelectedCampaignId(null)}
                                >
                                    <i className="fi fi-rr-arrow-left"></i> Back to All Events
                                </button>
                                <div style={{ display: "flex", gap: 10 }}>
                                    <button
                                        type="button"
                                        className="admin-bml-btn secondary"
                                        style={{ padding: "6px 12px", fontSize: 13 }}
                                        onClick={() => openEditCampaign(activeCampaign)}
                                    >
                                        <i className="fi fi-rr-edit"></i> Edit Event Details
                                    </button>
                                    <a
                                        href="/app-store/brand-my-laptop"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="admin-bml-btn secondary"
                                        style={{ padding: "6px 12px", fontSize: 13 }}
                                    >
                                        <i className="fi fi-rr-eye"></i> View Public Page
                                    </a>
                                </div>
                            </div>

                            {/* Active Campaign Card */}
                            <div className="admin-bml-card">
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
                                    <div>
                                        <h2>
                                            <i className="fi fi-rr-laptop" style={{ color: "#2563eb" }}></i>
                                            {activeCampaign.title}
                                        </h2>
                                        <p style={{ margin: "-8px 0 16px", color: "#64748b", fontSize: 13 }}>
                                            {activeCampaign.description}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="admin-bml-btn secondary"
                                        onClick={() => openEditCampaign(activeCampaign)}
                                    >
                                        <i className="fi fi-rr-edit"></i> Edit Travel Details
                                    </button>
                                </div>

                                {/* Dynamic Package Selection Control */}
                                <div className="admin-package-selector-banner">
                                    <div className="admin-pkg-info">
                                        <span className="admin-pkg-label">EVENT PRICING PACKAGE:</span>
                                        <span className="admin-pkg-desc">
                                            {packages.find((p) => p.packageId === activeCampaign.packageType)?.description ||
                                                (packages[0]?.description || "Conference rates for tech summits and paper presentations.")}
                                        </span>
                                    </div>

                                    {/* If only 1 package exists in database, show that package alone! */}
                                    {packages.length <= 1 ? (
                                        <div className="admin-single-pkg-view">
                                            <div className="admin-pkg-single-badge">
                                                <i className="fi fi-rr-gem" style={{ color: "#2563eb" }}></i>
                                                <strong>{packages[0]?.name || "Brand Slot Package"}</strong>
                                                <span className="admin-pkg-active-pill">Active Rates</span>
                                            </div>
                                            <button
                                                type="button"
                                                className="admin-bml-btn secondary"
                                                style={{ padding: "6px 14px", fontSize: 12 }}
                                                onClick={() => setActiveViewTab("packages")}
                                            >
                                                <i className="fi fi-rr-edit"></i> Edit Package Pricing
                                            </button>
                                        </div>
                                    ) : (
                                        /* If user configured 2 or more packages, allow choosing between them */
                                        <div className="admin-pkg-btn-group">
                                            {packages.map((pkg) => (
                                                <button
                                                    key={pkg.packageId}
                                                    type="button"
                                                    className={`admin-pkg-toggle-btn ${(activeCampaign.packageType === pkg.packageId || (!activeCampaign.packageType && pkg.packageId === "brand_slot")) ? "active" : ""}`}
                                                    onClick={() => handlePackageChange(pkg.packageId)}
                                                >
                                                    <i className="fi fi-rr-gem"></i> {pkg.name}
                                                </button>
                                            ))}
                                            <button
                                                type="button"
                                                className="admin-bml-btn secondary"
                                                style={{ padding: "6px 12px", fontSize: 12, marginLeft: 6 }}
                                                onClick={() => setActiveViewTab("packages")}
                                            >
                                                <i className="fi fi-rr-edit"></i> Edit Rates
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Campaign Summary Grid */}
                                <div className="admin-campaign-summary">
                                    <div className="admin-summary-item">
                                        <span className="label">Destination / Event</span>
                                        <span className="value">{activeCampaign.destination}</span>
                                    </div>
                                    <div className="admin-summary-item">
                                        <span className="label">Status</span>
                                        <span className="value" style={{ textTransform: "uppercase", color: activeCampaign.status === "open" ? "#10b981" : "#f59e0b" }}>
                                            {activeCampaign.status} {activeCampaign.isExtended ? "(Extended)" : ""}
                                        </span>
                                    </div>
                                    <div className="admin-summary-item">
                                        <span className="label">Booked Slots</span>
                                        <span className="value">
                                            {bookedSlots.length} / {activeCampaign.slots.length} Slots
                                        </span>
                                    </div>
                                    <div className="admin-summary-item">
                                        <span className="label">Total Revenue</span>
                                        <span className="value" style={{ color: "#10b981" }}>
                                            ₹{bookedSlots.reduce((acc, s) => acc + getSlotInrPrice(s), 0).toLocaleString()}
                                            <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500, marginLeft: 6 }}>
                                                (${bookedSlots.reduce((acc, s) => acc + getSlotUsdPrice(s), 0).toLocaleString()} / €{bookedSlots.reduce((acc, s) => acc + getSlotEurPrice(s), 0).toLocaleString()})
                                            </span>
                                        </span>
                                    </div>
                                </div>

                                {/* Action controls */}
                                <div className="admin-campaign-actions">
                                    <button
                                        className={`admin-bml-btn ${activeCampaign.isExtended ? "warning" : "secondary"}`}
                                        onClick={handleToggleExtension}
                                    >
                                        <i className="fi fi-rr-clock"></i>
                                        {activeCampaign.isExtended ? "Cancel Deadline Extension" : "Extend Booking (Beyond Saturday)"}
                                    </button>

                                    {activeCampaign.status !== "closed" && (
                                        <button
                                            className="admin-bml-btn secondary"
                                            onClick={handleCloseBooking}
                                        >
                                            <i className="fi fi-rr-lock"></i> Close Booking Now
                                        </button>
                                    )}

                                    <button
                                        className="admin-bml-btn success"
                                        onClick={() => {
                                            setProofRecap(activeCampaign.proofOfWork?.recapText || "");
                                            setShowProofModal(true);
                                        }}
                                    >
                                        <i className="fi fi-rr-camera"></i> Post Trip Proof & Photos (Mark Done)
                                    </button>
                                </div>
                            </div>

                            {/* Slots Management Table Card */}
                            <div className="admin-bml-card">
                                <h2>
                                    <i className="fi fi-rr-apps" style={{ color: "#2563eb" }}></i>
                                    All {activeCampaign.slots.length} Surface & Accessory Slots
                                </h2>
                                <p style={{ fontSize: 13, color: "#64748b", margin: "-8px 0 16px" }}>
                                    Review bookings, change slot status (Available, Under Review, Accepted, Action Required, Rejected), and attach feedback messages for sponsors.
                                </p>

                                <div className="admin-table-scroll-wrapper">
                                    <table className="admin-slots-table">
                                        <thead>
                                            <tr>
                                                <th>Slot ID</th>
                                                <th>Section</th>
                                                <th>Slot Name</th>
                                                <th>Dimensions</th>
                                                <th>Price (INR / USD / EUR)</th>
                                                <th>Status & Workflow</th>
                                                <th>Sponsor Details & Feedback</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {activeCampaign.slots.map((slot) => {
                                                const inr = getSlotInrPrice(slot);
                                                const usd = getSlotUsdPrice(slot);
                                                const eur = getSlotEurPrice(slot);
                                                const currentStatus = slot.status || (slot.isBooked ? "under_review" : "available");

                                                return (
                                                    <tr key={slot.slotId}>
                                                        <td><strong>{slot.slotId}</strong></td>
                                                        <td style={{ textTransform: "capitalize" }}>{slot.section ? slot.section.replace("_", " ") : "lid"}</td>
                                                        <td>{slot.name}</td>
                                                        <td>{slot.dimensions}</td>
                                                        <td>
                                                            <strong style={{ color: "#0f172a" }}>₹{inr.toLocaleString()}</strong>
                                                            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                                                                ${usd.toLocaleString()} USD &bull; €{eur.toLocaleString()} EUR
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                                <select
                                                                    className="admin-slot-status-select"
                                                                    value={currentStatus}
                                                                    onChange={(e) => handleInitiateStatusChange(slot, e.target.value)}
                                                                >
                                                                    <option value="available">🟢 Available</option>
                                                                    <option value="under_review">🔵 Under Review</option>
                                                                    <option value="accepted">✅ Accepted</option>
                                                                    <option value="action_required">⚠️ Action Required</option>
                                                                    <option value="rejected">❌ Rejected</option>
                                                                </select>

                                                                <span className={`admin-status-badge badge-${currentStatus.replace("_", "-")}`}>
                                                                    {currentStatus.replace("_", " ").toUpperCase()}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {slot.isBooked && slot.sponsor?.companyName ? (
                                                                <div className="admin-sponsor-info">
                                                                    <strong>{slot.sponsor.companyName}</strong>
                                                                    {slot.sponsor.website && (
                                                                        <a href={slot.sponsor.website} target="_blank" rel="noreferrer">
                                                                            {slot.sponsor.website}
                                                                        </a>
                                                                    )}
                                                                    {slot.sponsor.contactEmail && (
                                                                        <div style={{ fontSize: 11, color: "#2563eb", marginTop: 2 }}>
                                                                            ✉️ {slot.sponsor.contactEmail}
                                                                        </div>
                                                                    )}
                                                                    {slot.statusMessage && (
                                                                        <div className="admin-status-note-box">
                                                                            <span style={{ fontWeight: 600, fontSize: 11, color: "#92400e" }}>Note:</span> {slot.statusMessage}
                                                                        </div>
                                                                    )}
                                                                    {slot.sponsor.logoUrl && (
                                                                        <img
                                                                            src={slot.sponsor.logoUrl.startsWith("http") ? slot.sponsor.logoUrl : `${API_ENDPOINTS.AUTH.LOGIN.replace("/api/auth/login", "")}${slot.sponsor.logoUrl}`}
                                                                            alt="Logo"
                                                                            className="admin-sponsor-thumb"
                                                                        />
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span style={{ color: "#94a3b8" }}>— Empty —</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {slot.isBooked ? (
                                                                <button
                                                                    className="admin-bml-btn secondary"
                                                                    style={{ padding: "4px 10px", fontSize: 12 }}
                                                                    onClick={() => handleResetSlot(slot.slotId)}
                                                                    title="Reset slot back to available"
                                                                >
                                                                    Reset Slot
                                                                </button>
                                                            ) : (
                                                                <span style={{ fontSize: 12, color: "#10b981", fontWeight: 600 }}>Ready</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}

            {/* Edit Travel Event Details Modal */}
            {showEditCampModal && (
                <div className="admin-modal-overlay" onClick={() => setShowEditCampModal(false)}>
                    <div className="admin-modal-box" onClick={(e) => e.stopPropagation()}>
                        <h2>Edit Travel Event Details</h2>
                        <p style={{ fontSize: 13, color: "#64748b", marginTop: -8, marginBottom: 16 }}>
                            Update your event title, travel destination, conference dates, and status.
                        </p>

                        <form onSubmit={handleSaveCampaignEdit}>
                            <div className="admin-form-group">
                                <label>Event / Tour Title *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. IMRC2026 - Ahmedabad"
                                    value={editCampTitle}
                                    onChange={(e) => setEditCampTitle(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="admin-form-group">
                                <label>Destination City & Venue *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Ahmedabad, India - Paper Conference"
                                    value={editCampDestination}
                                    onChange={(e) => setEditCampDestination(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="admin-form-group">
                                <label>Public Story / Description</label>
                                <textarea
                                    rows={3}
                                    placeholder="Describe where you will be presenting with your MacBook..."
                                    value={editCampDesc}
                                    onChange={(e) => setEditCampDesc(e.target.value)}
                                />
                            </div>

                            <div className="admin-modal-row-grid">
                                <div className="admin-form-group">
                                    <label>Trip Start Date</label>
                                    <input
                                        type="date"
                                        value={editCampStartDate}
                                        onChange={(e) => setEditCampStartDate(e.target.value)}
                                    />
                                </div>
                                <div className="admin-form-group">
                                    <label>Trip End Date</label>
                                    <input
                                        type="date"
                                        value={editCampEndDate}
                                        onChange={(e) => setEditCampEndDate(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="admin-modal-row-grid">
                                <div className="admin-form-group">
                                    <label>Booking Open Date</label>
                                    <input
                                        type="date"
                                        value={editCampBookingOpenDate}
                                        onChange={(e) => setEditCampBookingOpenDate(e.target.value)}
                                    />
                                </div>
                                <div className="admin-form-group">
                                    <label>Booking Cutoff Date</label>
                                    <input
                                        type="date"
                                        value={editCampBookingCloseDate}
                                        onChange={(e) => setEditCampBookingCloseDate(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="admin-modal-row-grid">
                                <div className="admin-form-group">
                                    <label>Event Status</label>
                                    <select
                                        value={editCampStatus}
                                        onChange={(e) => setEditCampStatus(e.target.value)}
                                    >
                                        <option value="open">Open for Bookings</option>
                                        <option value="extended">Extended Booking</option>
                                        <option value="closed">Closed</option>
                                        <option value="completed">Completed</option>
                                    </select>
                                </div>
                                <div className="admin-form-group">
                                    <label>Pricing Package</label>
                                    <select
                                        value={editCampPackageType}
                                        onChange={(e) => setEditCampPackageType(e.target.value)}
                                    >
                                        {packages.map((pkg) => (
                                            <option key={pkg.packageId} value={pkg.packageId}>
                                                {pkg.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="admin-form-group" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                                <input
                                    type="checkbox"
                                    id="editIsExtended"
                                    checked={editCampIsExtended}
                                    onChange={(e) => setEditCampIsExtended(e.target.checked)}
                                    style={{ width: 18, height: 18 }}
                                />
                                <label htmlFor="editIsExtended" style={{ margin: 0, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                                    Extend Booking Deadline Notice
                                </label>
                            </div>

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
                                <button
                                    type="button"
                                    className="admin-bml-btn secondary"
                                    onClick={() => setShowEditCampModal(false)}
                                    disabled={savingCampEdit}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="admin-bml-btn primary"
                                    disabled={savingCampEdit}
                                >
                                    {savingCampEdit ? "Saving Changes..." : "Save Event Details"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create New Pricing Package Modal */}
            {showCreatePkgModal && (
                <div className="admin-modal-overlay" onClick={() => setShowCreatePkgModal(false)}>
                    <div className="admin-modal-box" onClick={(e) => e.stopPropagation()}>
                        <h2>Create New Pricing Package</h2>
                        <p style={{ fontSize: 13, color: "#64748b", marginTop: -8, marginBottom: 16 }}>
                            Create an alternate pricing plan (e.g. Regular Event Package) with custom slot rates.
                        </p>

                        <form onSubmit={handleCreatePackage}>
                            <div className="admin-form-group">
                                <label>Package Name *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Regular Event Package"
                                    value={newPkgName}
                                    onChange={(e) => setNewPkgName(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="admin-form-group">
                                <label>Package Description</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Budget rates configured for monthly meetups."
                                    value={newPkgDesc}
                                    onChange={(e) => setNewPkgDesc(e.target.value)}
                                />
                            </div>

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
                                <button
                                    type="button"
                                    className="admin-bml-btn secondary"
                                    onClick={() => setShowCreatePkgModal(false)}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="admin-bml-btn primary">
                                    Create Package
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Status Feedback Message Modal */}
            {statusModalSlot && (
                <div className="admin-modal-overlay" onClick={() => setStatusModalSlot(null)}>
                    <div className="admin-modal-box" onClick={(e) => e.stopPropagation()}>
                        <h2>
                            {statusTargetStatus === "rejected" ? "❌ Reject Slot Booking" : "⚠️ Request Sponsor Action"}
                        </h2>
                        <p style={{ fontSize: 13, color: "#64748b", marginTop: -8, marginBottom: 16 }}>
                            Slot: <strong>{statusModalSlot.name}</strong> ({statusModalSlot.slotId}) • Sponsor: <strong>{statusModalSlot.sponsor?.companyName || "Unknown"}</strong>
                        </p>

                        <div className="admin-form-group">
                            <label>Feedback / Instructions for Sponsor *</label>
                            <textarea
                                rows={4}
                                placeholder={
                                    statusTargetStatus === "rejected"
                                        ? "Explain reason for rejection (e.g. Inappropriate branding, logo overlaps existing hardware elements)..."
                                        : "Explain what action is required (e.g. Please re-upload logo with a transparent PNG background or higher resolution)..."
                                }
                                value={statusFeedbackMessage}
                                onChange={(e) => setStatusFeedbackMessage(e.target.value)}
                                required
                            />
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
                            <button
                                type="button"
                                className="admin-bml-btn secondary"
                                onClick={() => setStatusModalSlot(null)}
                                disabled={savingStatus}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={`admin-bml-btn ${statusTargetStatus === "rejected" ? "secondary" : "warning"}`}
                                style={statusTargetStatus === "rejected" ? { background: "#ef4444", color: "#fff" } : {}}
                                disabled={savingStatus}
                                onClick={() => executeStatusUpdate(statusModalSlot.slotId, statusTargetStatus, statusFeedbackMessage)}
                            >
                                {savingStatus ? "Saving..." : `Confirm ${statusTargetStatus === "rejected" ? "Rejection" : "Action Required"}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* New Weekly Campaign Modal */}
            {showNewModal && (
                <div className="admin-modal-overlay" onClick={() => setShowNewModal(false)}>
                    <div className="admin-modal-box" onClick={(e) => e.stopPropagation()}>
                        <h2>Create New Weekly Laptop Campaign</h2>
                        <p style={{ fontSize: 13, color: "#64748b", marginTop: -8, marginBottom: 16 }}>
                            Set up your next trip or conference to open slot reservations for tech brands.
                        </p>

                        <form onSubmit={handleCreateCampaign}>
                            <div className="admin-form-group">
                                <label>Campaign / Tour Title *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. IMRC2026 - Ahmedabad"
                                    value={newTitle}
                                    onChange={(e) => setNewTitle(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="admin-form-group">
                                <label>Destination City & Venue *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Ahmedabad, Gujarat - National Paper Presentation"
                                    value={newDestination}
                                    onChange={(e) => setNewDestination(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="admin-form-group">
                                <label>Initial Pricing Package</label>
                                <select
                                    value={newPackageType}
                                    onChange={(e) => setNewPackageType(e.target.value)}
                                >
                                    {packages.map((pkg) => (
                                        <option key={pkg.packageId} value={pkg.packageId}>
                                            {pkg.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="admin-form-group">
                                <label>Public Story / Description</label>
                                <textarea
                                    rows={3}
                                    placeholder="I am presenting my paper on AI at IMRC 2026..."
                                    value={newDesc}
                                    onChange={(e) => setNewDesc(e.target.value)}
                                />
                            </div>

                            <div className="admin-modal-row-grid">
                                <div className="admin-form-group">
                                    <label>Trip Start Date</label>
                                    <input
                                        type="date"
                                        value={newStartDate}
                                        onChange={(e) => setNewStartDate(e.target.value)}
                                    />
                                </div>
                                <div className="admin-form-group">
                                    <label>Trip End Date</label>
                                    <input
                                        type="date"
                                        value={newEndDate}
                                        onChange={(e) => setNewEndDate(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="admin-form-group">
                                <label>Booking Cutoff Date</label>
                                <input
                                    type="date"
                                    value={newBookingCloseDate}
                                    onChange={(e) => setNewBookingCloseDate(e.target.value)}
                                />
                            </div>

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
                                <button
                                    type="button"
                                    className="admin-bml-btn secondary"
                                    onClick={() => setShowNewModal(false)}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="admin-bml-btn primary">
                                    Launch Weekly Event
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Proof of Work Modal */}
            {showProofModal && activeCampaign && (
                <div className="admin-modal-overlay" onClick={() => setShowProofModal(false)}>
                    <div className="admin-modal-box" onClick={(e) => e.stopPropagation()}>
                        <h2>Post Trip Proof & Branded Laptop Photos</h2>
                        <p style={{ fontSize: 13, color: "#64748b", marginTop: -8, marginBottom: 16 }}>
                            Upload real photos of your MacBook Pro stickered with sponsor brands from the trip. This will be published to the public history archive!
                        </p>

                        <form onSubmit={handleSubmitProof}>
                            <div className="admin-form-group">
                                <label>Trip Recap & Highlights</label>
                                <textarea
                                    rows={4}
                                    placeholder="Write a recap of your trip, founder meetings, demos given on the laptop..."
                                    value={proofRecap}
                                    onChange={(e) => setProofRecap(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="admin-form-group">
                                <label>Upload Trip Photos with Laptop (up to 5 photos)</label>
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    onChange={(e) => setProofFiles(e.target.files)}
                                />
                            </div>

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
                                <button
                                    type="button"
                                    className="admin-bml-btn secondary"
                                    onClick={() => setShowProofModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="admin-bml-btn success"
                                    disabled={submittingProof}
                                >
                                    {submittingProof ? "Uploading & Publishing..." : "Publish to History"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
